import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Plane,
  LayoutDashboard,
  Wallet,
  Clock,
  CreditCard,
  CheckCircle,
  XCircle,
  AlertTriangle,
  FileText,
  Plus,
  ArrowRight,
  TrendingUp,
  Building2,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TravelDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/travel-desk/dashboard`, { withCredentials: true });
      setDashboardData(data);
    } catch (err) {
      toast.error('Failed to load travel dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center items-center min-h-[600px]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm font-semibold text-slate-600">Loading Travel Dashboard...</p>
        </div>
      </div>
    );
  }

  const kpis = dashboardData?.kpis || {};
  const deptSpend = dashboardData?.department_spend || {};
  const recentReqs = dashboardData?.recent_requests || [];

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto font-sans">
      {/* Top Banner Header */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 text-white rounded-2xl p-6 shadow-2xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border border-slate-800">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-500/20 text-amber-400 rounded-xl border border-amber-500/30">
              <Plane className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-black tracking-tight">Travel Desk Dashboard</h1>
              <p className="text-xs text-blue-200">
                Corporate Travel Analytics · Key Performance Indicators · Travel Spend & Utilization
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={() => navigate('/travel/request/new')}
            className="px-4 py-2.5 bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-slate-950 font-bold text-xs rounded-xl shadow-lg flex items-center gap-2 transition-all transform hover:-translate-y-0.5"
          >
            <Plus className="w-4 h-4" /> Raise Travel Request
          </button>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Total Requests */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow space-y-2">
          <div className="flex justify-between items-center text-slate-500 text-xs font-bold uppercase tracking-wider">
            <span>Total Requests</span>
            <FileText className="w-4 h-4 text-blue-600" />
          </div>
          <div className="text-3xl font-black text-slate-900">{kpis.total_requests || 0}</div>
          <div className="flex items-center gap-2 text-[11px] text-slate-500 font-medium pt-1 border-t border-slate-100">
            <span className="text-amber-600 font-bold">Pending: {kpis.pending_requests || 0}</span>
            <span>•</span>
            <span className="text-emerald-600 font-bold">Approved: {kpis.approved_requests || 0}</span>
          </div>
        </div>

        {/* Card 2: Total Travel Spend */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow space-y-2">
          <div className="flex justify-between items-center text-slate-500 text-xs font-bold uppercase tracking-wider">
            <span>Total Travel Spend</span>
            <Wallet className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="text-3xl font-black text-emerald-600">
            ₹ {(kpis.total_travel_spend || 0).toLocaleString('en-IN')}
          </div>
          <div className="text-[11px] text-slate-500 font-medium pt-1 border-t border-slate-100">
            Confirmed travel & ticket bookings
          </div>
        </div>

        {/* Card 3: Credit Card Utilization */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow space-y-2">
          <div className="flex justify-between items-center text-slate-500 text-xs font-bold uppercase tracking-wider">
            <span>Credit Card Utilization</span>
            <CreditCard className="w-4 h-4 text-indigo-600" />
          </div>
          <div className="text-3xl font-black text-indigo-600">
            {kpis.card_utilization_pct || 0}%
          </div>
          <div className="text-[11px] text-slate-500 font-medium pt-1 border-t border-slate-100">
            Avail. Limit: <b className="text-slate-800">₹ {(kpis.available_credit_limit || 0).toLocaleString('en-IN')}</b>
          </div>
        </div>

        {/* Card 4: Trip Breakdown */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow space-y-2">
          <div className="flex justify-between items-center text-slate-500 text-xs font-bold uppercase tracking-wider">
            <span>Trip Breakdown</span>
            <TrendingUp className="w-4 h-4 text-purple-600" />
          </div>
          <div className="text-2xl font-black text-slate-800">
            {kpis.domestic_trips || 0} <span className="text-xs font-normal text-slate-500">Domestic</span> / {kpis.international_trips || 0} <span className="text-xs font-normal text-slate-500">Intl</span>
          </div>
          <div className="text-[11px] text-slate-500 font-medium pt-1 border-t border-slate-100">
            Cancelled Trips: <b className="text-rose-600">{kpis.cancelled_requests || 0}</b>
          </div>
        </div>
      </div>

      {/* Main Charts & Activity Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Department Travel Spend Breakdown */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
              <Building2 className="w-4 h-4 text-emerald-600" /> Department Travel Spend Breakdown
            </h3>
            <span className="text-xs text-slate-400 font-medium">All Time</span>
          </div>

          <div className="space-y-4 pt-2">
            {Object.keys(deptSpend).length === 0 ? (
              <p className="text-xs text-slate-400 italic py-4">No departmental spend recorded yet.</p>
            ) : (
              Object.entries(deptSpend).map(([dept, amt]) => {
                const total = kpis.total_travel_spend || 1;
                const pct = Math.min(100, Math.round((amt / total) * 100));
                return (
                  <div key={dept} className="space-y-1.5">
                    <div className="flex justify-between text-xs font-semibold text-slate-700">
                      <span className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block"></span>
                        {dept}
                      </span>
                      <span className="font-mono font-bold text-slate-900">
                        ₹ {amt.toLocaleString('en-IN')} <span className="text-slate-400 text-[10px]">({pct}%)</span>
                      </span>
                    </div>
                    <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-emerald-500 to-teal-400 rounded-full transition-all duration-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Recent Requests Panel */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-4 flex flex-col justify-between">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-600" /> Recent Travel Requests
              </h3>
              <button
                onClick={() => navigate('/travel/requests')}
                className="text-xs font-bold text-blue-600 hover:underline flex items-center gap-1"
              >
                View All <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="space-y-2.5 max-h-[380px] overflow-y-auto pr-1">
              {recentReqs.length === 0 ? (
                <p className="text-xs text-slate-400 italic py-4">No recent requests found.</p>
              ) : (
                recentReqs.map((r) => (
                  <div
                    key={r._id}
                    onClick={() => navigate(`/travel/request/${r._id}`)}
                    className="p-3 bg-slate-50 hover:bg-blue-50/60 rounded-xl border border-slate-100 transition-all cursor-pointer flex items-center justify-between text-xs"
                  >
                    <div className="space-y-0.5">
                      <div className="font-bold text-slate-900">{r.employee_name}</div>
                      <div className="text-slate-500 text-[11px]">
                        <span className="font-mono text-blue-600 font-bold">{r.request_number}</span> · {r.purpose}
                      </div>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold capitalize shrink-0 ${
                      r.status === 'approved' || r.status === 'booked'
                        ? 'bg-emerald-100 text-emerald-800'
                        : r.status === 'rejected'
                        ? 'bg-rose-100 text-rose-800'
                        : 'bg-amber-100 text-amber-800'
                    }`}>
                      {r.status}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="pt-4 border-t border-slate-100">
            <button
              onClick={() => navigate('/travel/approval')}
              className="w-full py-2 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl flex items-center justify-center gap-2 shadow-sm transition-all"
            >
              Go to Approval Inbox &rarr;
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TravelDashboard;
